/**
 * Report Distribution Page — Items 3, 4, 5
 * Per-MCO report config, generation, preview, SFTP delivery, approval workflow.
 */

import React, { useState, useEffect } from 'react';
import {
  FileText, Plus, Download, Settings, CheckCircle, XCircle, Clock,
  Send, Eye, Loader2, Trash2, Wifi, WifiOff, AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader, PageContainer, PageState } from '@/components/weavein';
import { apiClient } from '@/services/apiClient';

interface ReportConfig {
  id: number; report_name: string; report_type: string;
  included_fields: string[]; field_order: string[] | null;
  field_display_names: Record<string, string>;
  output_format: string; delimiter: string;
  filename_template: string | null; include_header_row: boolean;
  date_format: string; frequency: string | null;
  delivery_method: string | null; sftp_host: string | null;
  sftp_port: number; sftp_username: string | null;
  sftp_remote_dir: string | null;
  requires_approval: boolean; approver_user_ids: number[] | null;
  is_active: boolean;
  payer_name?: string;
  program_name?: string;
  saved_report_id?: number | null;
  saved_report_name?: string | null;
}

interface SavedReport {
  id: number; name: string; description: string | null;
  data_source: string; is_public: boolean; last_run_at: string | null;
}

interface AffiliationWithMco {
  id: number; program_id: number; payer_id: number;
  payer_name: string | null; program_name: string | null;
  health_plan_name: string | null; affiliation_type: string;
  status: string; report_config_count: number;
}

interface ReportApproval {
  id: number; report_config_id: number; report_name: string | null;
  file_name: string | null; generated_by_name: string | null;
  generated_at: string; status: string;
  reviewer_name: string | null; reviewed_at: string | null;
  review_notes: string | null;
}

interface PreviewData {
  columns: string[]; rows: Record<string, string>[]; total_preview: number;
}

const REPORT_TYPES = [
  { value: 'saved_report', label: 'Saved / Custom Report' },
  { value: 'member_roster', label: 'Member Roster' },
  { value: 'enrollment_report', label: 'Enrollment Report' },
  { value: 'billing_summary', label: 'Billing Summary' },
  { value: 'activity_log', label: 'Activity Log' },
  { value: 'mco_delegation', label: 'MCO Delegation Report' },
  { value: 'campaign_progress', label: 'Campaign Progress (engagement_events)' },
  { value: 'campaign_overview', label: 'Campaign Overview (by LOB)' },
];

const DELIMITERS = [
  { value: ',', label: 'Comma (,)' },
  { value: '|', label: 'Pipe (|)' },
  { value: '\\t', label: 'Tab' },
  { value: ';', label: 'Semicolon (;)' },
];

const DATE_FORMATS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
];

const AVAILABLE_FIELDS: Record<string, string[]> = {
  member_roster: ['id', 'member_number', 'first_name', 'last_name', 'email', 'phone_no', 'dob', 'gender', 'race', 'ethnicity', 'language', 'medicaid_id', 'medicare_id', 'street_address', 'city', 'state', 'zip_code', 'data_quality_score', 'primary_coordinator_id'],
  enrollment_report: ['enrollment_id', 'member_number', 'first_name', 'last_name', 'medicaid_id', 'dob', 'gender', 'program_name', 'enrollment_status', 'enrollment_date', 'discharge_date', 'case_manager_name'],
  billing_summary: ['period_month', 'period_year', 'eligible_member_count', 'rate_per_member', 'total_amount', 'status', 'rate_name', 'mco_id'],
  activity_log: ['id', 'title', 'status', 'priority', 'due_date', 'member_name', 'member_number', 'assigned_to', 'created_at'],
  mco_delegation: ['member_number', 'first_name', 'last_name', 'medicaid_id', 'mco_id', 'mco_name', 'delegated_date', 'delegation_source', 'status', 'terminated_date'],
  campaign_progress: ['campaign_name', 'lob_name', 'product_name', 'engagement_order', 'engagement_name', 'channel', 'language', 'targeted', 'delivered', 'undelivered', 'responded', 'opted_out', 'undelivered_reasons'],
  campaign_overview: ['lob_name', 'product_name', 'campaign_name', 'sent', 'delivered', 'delivery_rate', 'responded', 'response_rate', 'opted_out', 'bounced', 'window'],
};

import { UUHPCampaignProgressPage } from '../../../../components/communications/EngagementDemoFlowComponents';

export default function ReportDistributionPage() {
  const [activeTab, setActiveTab] = useState<string>(() => {
    if (typeof window !== 'undefined' && window.location.hash === '#campaign-progress') {
      return 'campaign-progress';
    }
    return 'configs';
  });
  const activeCboId = typeof window !== 'undefined'
    ? parseInt(localStorage.getItem('active_cbo_id') || '0', 10) || 303
    : 303;
  const [configs, setConfigs] = useState<ReportConfig[]>([]);
  const [approvals, setApprovals] = useState<ReportApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // MCO affiliation picker state
  const [affiliations, setAffiliations] = useState<AffiliationWithMco[]>([]);
  const [selectedAffiliationId, setSelectedAffiliationId] = useState<string>('');

  // Saved reports for "Saved / Custom Report" type
  const [savedReports, setSavedReports] = useState<SavedReport[]>([]);
  const [formSavedReportId, setFormSavedReportId] = useState<string>('');

  // Create form state
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState('member_roster');
  const [formFields, setFormFields] = useState<string[]>([]);
  const [formDisplayNames, setFormDisplayNames] = useState<Record<string, string>>({});
  const [formDelimiter, setFormDelimiter] = useState(',');
  const [formDateFormat, setFormDateFormat] = useState('MM/DD/YYYY');
  const [formFilenameTemplate, setFormFilenameTemplate] = useState('');
  const [formIncludeHeader, setFormIncludeHeader] = useState(true);
  const [formDelivery, setFormDelivery] = useState('download');
  const [formSftpHost, setFormSftpHost] = useState('');
  const [formSftpPort, setFormSftpPort] = useState('22');
  const [formSftpUser, setFormSftpUser] = useState('');
  const [formSftpDir, setFormSftpDir] = useState('');
  const [formRequiresApproval, setFormRequiresApproval] = useState(false);
  const [saving, setSaving] = useState(false);

  // Preview / generate state
  const [previewConfigId, setPreviewConfigId] = useState<number | null>(null);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generating, setGenerating] = useState<number | null>(null);
  const [delivering, setDelivering] = useState<number | null>(null);
  const [testingConnection, setTestingConnection] = useState<number | null>(null);
  const [connectionResult, setConnectionResult] = useState<any>(null);

  const fetchAffiliations = async () => {
    try {
      const cboId = localStorage.getItem('active_cbo_id') || '1';
      const resp = await apiClient.get(`/api/organization-setup/${cboId}/affiliations`);
      const affs = Array.isArray(resp.data) ? resp.data : [];
      setAffiliations(affs);
      // Auto-select first affiliation if none selected
      if (affs.length > 0 && !selectedAffiliationId) {
        setSelectedAffiliationId(String(affs[0].id));
      }
    } catch { setAffiliations([]); }
  };

  const fetchData = async () => {
    if (!selectedAffiliationId) { setConfigs([]); setApprovals([]); setLoading(false); return; }
    setLoading(true);
    try {
      const [cfgResp, appResp] = await Promise.all([
        apiClient.get(`/api/organization-setup/affiliations/${selectedAffiliationId}/report-configs`).catch(() => ({ data: [] })),
        apiClient.get('/api/organization-setup/report-approvals').catch(() => ({ data: [] })),
      ]);
      setConfigs(Array.isArray(cfgResp.data) ? cfgResp.data : []);
      setApprovals(Array.isArray(appResp.data) ? appResp.data : []);
    } finally { setLoading(false); }
  };

  const fetchSavedReports = async () => {
    try {
      const resp = await apiClient.get('/api/analytics/reports/saved');
      const reports = Array.isArray(resp.data) ? resp.data : resp.data?.reports || [];
      setSavedReports(reports);
    } catch { setSavedReports([]); }
  };

  useEffect(() => { Promise.all([fetchAffiliations(), fetchSavedReports()]).finally(() => setLoading(false)); }, []);
  useEffect(() => { if (selectedAffiliationId) fetchData(); }, [selectedAffiliationId]);

  const createConfig = async () => {
    if (!selectedAffiliationId) { alert('Please select an MCO affiliation first'); return; }
    if (!formName) { alert('Report name is required'); return; }
    if (formType === 'saved_report' && !formSavedReportId) { alert('Please select a saved report'); return; }
    if (formType !== 'saved_report' && formFields.length === 0) { alert('At least one field is required'); return; }
    setSaving(true);
    try {
      const isSaved = formType === 'saved_report' && formSavedReportId;
      await apiClient.post(`/api/organization-setup/affiliations/${selectedAffiliationId}/report-configs`, {
        report_name: formName, report_type: isSaved ? 'saved_report' : formType,
        included_fields: isSaved ? ['*'] : formFields, field_display_names: formDisplayNames,
        delimiter: formDelimiter, date_format: formDateFormat,
        filename_template: formFilenameTemplate || null,
        include_header_row: formIncludeHeader,
        delivery_method: formDelivery,
        sftp_host: formSftpHost || null, sftp_port: parseInt(formSftpPort),
        sftp_username: formSftpUser || null, sftp_remote_dir: formSftpDir || null,
        requires_approval: formRequiresApproval,
        saved_report_id: isSaved ? parseInt(formSavedReportId) : null,
      });
      setShowCreateForm(false);
      setFormName(''); setFormFields([]); setFormDisplayNames({});
      await fetchData();
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed to create config'); }
    finally { setSaving(false); }
  };

  const previewReport = async (configId: number) => {
    setPreviewConfigId(configId);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const resp = await apiClient.get(`/api/organization-setup/report-configs/${configId}/preview?max_rows=10`);
      setPreviewData(resp.data);
    } catch (e: any) { alert(e.response?.data?.detail || 'Preview failed'); }
    finally { setPreviewLoading(false); }
  };

  const generateReport = async (configId: number) => {
    setGenerating(configId);
    try {
      const resp = await apiClient.post(`/api/organization-setup/report-configs/${configId}/generate`, {}, { responseType: 'blob' });
      if (String(resp.headers['content-type'] ?? '').includes('application/json')) {
        const text = await (resp.data as Blob).text();
        const data = JSON.parse(text);
        if (data.status === 'pending_approval') {
          alert(`Report queued for approval (ID: ${data.approval_id}). ${data.row_count} rows.`);
          await fetchData();
          return;
        }
      }
      const blob = new Blob([resp.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'report.csv';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) { alert(e.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(null); }
  };

  const deliverViaSftp = async (configId: number) => {
    setDelivering(configId);
    try {
      const resp = await apiClient.post(`/api/organization-setup/report-configs/${configId}/deliver`);
      const d = resp.data;
      if (d.delivery?.success) {
        alert(`Delivered! ${d.generated?.row_count} rows → ${d.delivery?.remote_path}`);
      } else {
        alert(`Delivery failed: ${d.delivery?.error}`);
      }
    } catch (e: any) { alert(e.response?.data?.detail || 'Delivery failed'); }
    finally { setDelivering(null); }
  };

  const testSftp = async (configId: number) => {
    setTestingConnection(configId);
    setConnectionResult(null);
    try {
      const resp = await apiClient.post(`/api/organization-setup/report-configs/${configId}/test-sftp`);
      setConnectionResult(resp.data);
    } catch (e: any) { setConnectionResult({ success: false, error: e.response?.data?.detail || 'Test failed' }); }
    finally { setTestingConnection(null); }
  };

  const approveReport = async (approvalId: number) => {
    try {
      await apiClient.put(`/api/organization-setup/report-approvals/${approvalId}/approve`);
      await fetchData();
    } catch (e: any) { alert(e.response?.data?.detail || 'Approval failed'); }
  };

  const rejectReport = async (approvalId: number) => {
    const notes = prompt('Rejection reason:');
    if (notes === null) return;
    try {
      await apiClient.put(`/api/organization-setup/report-approvals/${approvalId}/reject?notes=${encodeURIComponent(notes)}`);
      await fetchData();
    } catch (e: any) { alert(e.response?.data?.detail || 'Rejection failed'); }
  };

  const toggleField = (field: string) => {
    setFormFields(prev => prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]);
  };

  const availableFields = AVAILABLE_FIELDS[formType] || [];
  const pendingApprovals = approvals.filter(a => a.status === 'pending');

  const header = (
    <PageHeader
      eyebrow="Reports · Distribution"
      title="Report Distribution"
      description="Configure per-LOB / plan reports with custom formatting, SFTP delivery, and approval workflows"
    />
  );

  if (loading) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <PageContainer>
          <PageState variant="loading" title="Loading report distribution" />
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {header}
      <PageContainer>
      {/* LOB / Plan affiliation picker (formerly MCO affiliation) */}
      <ReportLobAffiliationPicker
        affiliations={affiliations}
        selectedAffiliationId={selectedAffiliationId}
        onSelect={setSelectedAffiliationId}
        loading={loading}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="campaign-progress">
            Campaign Progress
          </TabsTrigger>
          <TabsTrigger value="configs">
            Report Configs <Badge variant="secondary" className="ml-1">{configs.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="approvals">
            Approvals {pendingApprovals.length > 0 && <Badge variant="destructive" className="ml-1">{pendingApprovals.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* ============ CAMPAIGN PROGRESS TAB ============ */}
        <TabsContent value="campaign-progress" className="space-y-4">
          <UUHPCampaignProgressPage cboId={activeCboId} />
        </TabsContent>

        {/* ============ CONFIGS TAB ============ */}
        <TabsContent value="configs" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowCreateForm(!showCreateForm)}><Plus className="w-4 h-4 mr-1" /> New Report Config</Button>
          </div>

          {/* Create Form */}
          {showCreateForm && (
            <Card className="border-blue-200 bg-blue-50/30">
              <CardContent className="py-5 space-y-4">
                <h3 className="font-semibold text-lg">Create Report Configuration</h3>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Report Name *</Label>
                    <Input value={formName} onChange={e => setFormName(e.target.value)} placeholder="Monthly Member Roster" className="h-8" />
                  </div>
                  <div>
                    <Label className="text-xs">Report Type *</Label>
                    <Select value={formType} onValueChange={(v) => { setFormType(v); setFormFields([]); }}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {REPORT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {formType === 'saved_report' ? (
                    <div>
                      <Label className="text-xs">Saved Report *</Label>
                      <Select value={formSavedReportId} onValueChange={setFormSavedReportId}>
                        <SelectTrigger className="h-8"><SelectValue placeholder="Select a saved report..." /></SelectTrigger>
                        <SelectContent>
                          {savedReports.length === 0 ? (
                            <SelectItem value="_none" disabled>No saved reports found</SelectItem>
                          ) : (
                            savedReports.map(r => (
                              <SelectItem key={r.id} value={String(r.id)}>
                                {r.name} ({r.data_source})
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div>
                      <Label className="text-xs">Delimiter</Label>
                      <Select value={formDelimiter} onValueChange={setFormDelimiter}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DELIMITERS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs">Date Format</Label>
                    <Select value={formDateFormat} onValueChange={setFormDateFormat}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DATE_FORMATS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Field Selection */}
                {formType === 'saved_report' ? (
                  <div className="p-3 bg-paper rounded-lg border text-xs text-ink-500">
                    Fields are defined by the saved report configuration. All columns from the saved report will be included.
                    {formSavedReportId && savedReports.find(r => String(r.id) === formSavedReportId) && (
                      <span className="block mt-1 font-medium text-ink-700">
                        Source: {savedReports.find(r => String(r.id) === formSavedReportId)!.data_source}
                        {savedReports.find(r => String(r.id) === formSavedReportId)!.description && (
                          <> — {savedReports.find(r => String(r.id) === formSavedReportId)!.description}</>
                        )}
                      </span>
                    )}
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs">Included Fields * — click to select ({formFields.length} selected)</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1 p-2 border rounded-lg bg-surface max-h-32 overflow-y-auto">
                      {availableFields.map(f => (
                        <button key={f} type="button" onClick={() => toggleField(f)}
                          className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                            formFields.includes(f) ? 'bg-blue-600 text-white' : 'bg-paper-2 text-ink-500 hover:bg-paper-2'
                          }`}>{f}</button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Filename + Header */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Filename Template</Label>
                    <Input value={formFilenameTemplate} onChange={e => setFormFilenameTemplate(e.target.value)} placeholder="{report_name}_{date:YYYYMMDD}.csv" className="h-8 text-xs" />
                    <p className="text-[10px] text-ink-400 mt-0.5">Variables: {'{report_name}'}, {'{report_type}'}, {'{date:YYYYMMDD}'}, {'{period}'}</p>
                  </div>
                  <div className="flex items-center gap-2 pt-4">
                    <input type="checkbox" checked={formIncludeHeader} onChange={e => setFormIncludeHeader(e.target.checked)} id="header-row" />
                    <Label htmlFor="header-row" className="text-xs cursor-pointer">Include header row</Label>
                  </div>
                  <div className="flex items-center gap-2 pt-4">
                    <input type="checkbox" checked={formRequiresApproval} onChange={e => setFormRequiresApproval(e.target.checked)} id="requires-approval" />
                    <Label htmlFor="requires-approval" className="text-xs cursor-pointer">Requires supervisor approval</Label>
                  </div>
                </div>

                {/* Delivery Method */}
                <div>
                  <Label className="text-xs">Delivery Method</Label>
                  <Select value={formDelivery} onValueChange={setFormDelivery}>
                    <SelectTrigger className="w-48 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="download">Download</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sftp">SFTP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* SFTP Config */}
                {formDelivery === 'sftp' && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 border border-blue-200 rounded-lg bg-blue-50/20">
                    <div><Label className="text-xs">SFTP Host *</Label><Input value={formSftpHost} onChange={e => setFormSftpHost(e.target.value)} placeholder="sftp.mco.com" className="h-8 text-xs" /></div>
                    <div><Label className="text-xs">Port</Label><Input value={formSftpPort} onChange={e => setFormSftpPort(e.target.value)} type="number" className="h-8 text-xs" /></div>
                    <div><Label className="text-xs">Username</Label><Input value={formSftpUser} onChange={e => setFormSftpUser(e.target.value)} placeholder="meca_uploads" className="h-8 text-xs" /></div>
                    <div><Label className="text-xs">Remote Directory</Label><Input value={formSftpDir} onChange={e => setFormSftpDir(e.target.value)} placeholder="/inbound/reports/" className="h-8 text-xs" /></div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button onClick={createConfig} disabled={!formName || (formType === 'saved_report' ? !formSavedReportId : formFields.length === 0) || saving}>{saving ? 'Creating...' : 'Create Report Config'}</Button>
                  <Button variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Config List */}
          {configs.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-ink-400"><FileText className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No report configs. Create one to get started.</p></CardContent></Card>
          ) : (
            <div className="space-y-3">
              {configs.map(cfg => (
                <Card key={cfg.id}>
                  <CardContent className="py-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{cfg.report_name}</span>
                          {cfg.payer_name && <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200">{cfg.payer_name}</Badge>}
                          <Badge variant="outline">{REPORT_TYPES.find(t => t.value === cfg.report_type)?.label || cfg.report_type}</Badge>
                          {cfg.saved_report_id && <Badge variant="outline" className="text-[10px] bg-teal-50 text-teal-700 border-teal-200">Custom Report #{cfg.saved_report_id}</Badge>}
                          {cfg.requires_approval && <Badge variant="secondary" className="text-[10px]">Approval Required</Badge>}
                          {cfg.delivery_method === 'sftp' && <Badge variant="secondary" className="text-[10px]">SFTP: {cfg.sftp_host}</Badge>}
                        </div>
                        <div className="text-xs text-ink-500 mt-1">
                          {cfg.included_fields?.length || 0} fields · Delimiter: {DELIMITERS.find(d => d.value === cfg.delimiter)?.label || cfg.delimiter} · Date: {cfg.date_format}
                          {cfg.filename_template && ` · File: ${cfg.filename_template}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" title="Preview" onClick={() => previewReport(cfg.id)} disabled={previewLoading}>
                          {previewLoading && previewConfigId === cfg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" title="Generate & Download" onClick={() => generateReport(cfg.id)} disabled={generating === cfg.id}>
                          {generating === cfg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        </Button>
                        {cfg.sftp_host && (
                          <>
                            <Button variant="ghost" size="sm" title="Test SFTP Connection" onClick={() => testSftp(cfg.id)} disabled={testingConnection === cfg.id}>
                              {testingConnection === cfg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
                            </Button>
                            <Button variant="ghost" size="sm" title="Generate & Deliver via SFTP" onClick={() => deliverViaSftp(cfg.id)} disabled={delivering === cfg.id}>
                              {delivering === cfg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* SFTP Connection Test Result */}
                    {connectionResult && testingConnection === null && previewConfigId !== cfg.id && (
                      <div className={`p-2 rounded text-xs ${connectionResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                        {connectionResult.success ? (
                          <span className="flex items-center gap-1"><Wifi className="w-3.5 h-3.5" /> Connected to {connectionResult.host} · {connectionResult.files_visible} files visible</span>
                        ) : (
                          <span className="flex items-center gap-1"><WifiOff className="w-3.5 h-3.5" /> {connectionResult.error}</span>
                        )}
                      </div>
                    )}

                    {/* Preview Panel */}
                    {previewConfigId === cfg.id && previewData && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-paper px-3 py-1.5 text-xs font-medium text-ink-500 flex justify-between">
                          <span>Preview — {previewData.total_preview} rows</span>
                          <button onClick={() => { setPreviewConfigId(null); setPreviewData(null); }} className="text-ink-400 hover:text-ink-500">Close</button>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-paper-2">
                              <tr>{previewData.columns.map(c => <th key={c} className="px-2 py-1 text-left font-medium">{c}</th>)}</tr>
                            </thead>
                            <tbody>
                              {previewData.rows.map((row, i) => (
                                <tr key={i} className="border-t hover:bg-paper">
                                  {previewData.columns.map(c => <td key={c} className="px-2 py-1">{row[c] || ''}</td>)}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ============ APPROVALS TAB ============ */}
        <TabsContent value="approvals" className="space-y-4">
          {approvals.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-ink-400"><CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No report approvals.</p></CardContent></Card>
          ) : (
            <div className="space-y-2">
              {approvals.map(a => (
                <Card key={a.id} className={a.status === 'pending' ? 'border-yellow-200' : a.status === 'approved' ? 'border-green-200' : 'border-red-200'}>
                  <CardContent className="py-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">{a.report_name || `Report #${a.report_config_id}`}</div>
                      <div className="text-xs text-ink-500">
                        {a.file_name} · Generated by {a.generated_by_name || '?'} · {a.generated_at ? new Date(a.generated_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                        {a.reviewer_name && ` · Reviewed by ${a.reviewer_name}`}
                        {a.review_notes && ` · Notes: ${a.review_notes}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={a.status === 'pending' ? 'default' : a.status === 'approved' ? 'default' : 'destructive'}
                        className={a.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : a.status === 'approved' ? 'bg-green-100 text-green-800' : ''}>
                        {a.status}
                      </Badge>
                      {a.status === 'pending' && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="text-green-600 h-7" onClick={() => approveReport(a.id)}><CheckCircle className="w-4 h-4 mr-1" /> Approve</Button>
                          <Button size="sm" variant="ghost" className="text-red-600 h-7" onClick={() => rejectReport(a.id)}><XCircle className="w-4 h-4 mr-1" /> Reject</Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      </PageContainer>
    </div>
  );
}

// ============================================================================
// ReportLobAffiliationPicker — surfaces UUHP lines-of-business & products
// alongside the legacy MCO affiliation list. The selectable value is still
// the affiliation_id (preserves the existing report_config FK), but the
// label resolves to the UUHP line of business + product name when that
// metadata is available so the picker reads as a plan/LOB selector
// instead of an MCO selector.
// ============================================================================
function ReportLobAffiliationPicker({
  affiliations,
  selectedAffiliationId,
  onSelect,
  loading,
}: {
  affiliations: AffiliationWithMco[];
  selectedAffiliationId: string;
  onSelect: (id: string) => void;
  loading: boolean;
}) {
  const [productLines, setProductLines] = useState<Array<{ lob_id: number; lob_name: string; products: Array<{ id: number; name: string }> }>>([]);

  useEffect(() => {
    const cboId = localStorage.getItem('active_cbo_id');
    if (!cboId) return;
    apiClient.get(`/api/orgs/${cboId}/product-lines`)
      .then(res => setProductLines(Array.isArray(res.data) ? res.data : []))
      .catch(() => setProductLines([]));
  }, []);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-sm font-medium whitespace-nowrap">Line of Business / Plan:</Label>
        <Select value={selectedAffiliationId} onValueChange={onSelect}>
          <SelectTrigger className="w-96 h-9">
            <SelectValue placeholder="Select a plan / LOB affiliation..." />
          </SelectTrigger>
          <SelectContent>
            {affiliations.map(aff => (
              <SelectItem key={aff.id} value={String(aff.id)}>
                {aff.health_plan_name || aff.payer_name || `Affiliation #${aff.id}`}
                {aff.program_name ? ` · ${aff.program_name}` : ''}
                {' '}({aff.report_config_count} configs)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {affiliations.length === 0 && !loading && (
          <span className="text-xs text-amber-600 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" />
            No active LOB affiliations. Configure plan affiliations to scope reports per LOB.
          </span>
        )}
      </div>
      {productLines.length > 0 && (
        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs">
          <div className="font-semibold text-blue-900 mb-1">Reports can be configured per line of business:</div>
          <div className="flex flex-wrap gap-1.5">
            {productLines.map(l => (
              <span key={l.lob_id} className="bg-surface rounded-full px-2 py-0.5 text-[11px] text-blue-800 border border-blue-200">
                {l.lob_name} <span className="opacity-60">({l.products.length} plan{l.products.length === 1 ? '' : 's'})</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
